// Web port of app/src/contexts/AuthenticationContext.js — keep in sync with it.
// The mobile provider is platform-agnostic (react + aws-amplify/auth v6, no React
// Native), so its logic is copied near-verbatim. Web v1 only needs sign-in,
// sign-out and forgot-password INITIATION, so the sign-up / email-verification /
// change-password / forgot-password-submit members are intentionally trimmed:
// those stay on mobile and the /password-code reset bridge. The value shape for
// the members that remain matches the app, except signInWithPasskey, which takes
// the email the user typed where the app uses the account it remembered.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  signIn as cognitoSignIn,
  confirmSignIn as cognitoConfirmSignIn,
  signOut as cognitoSignOut,
  resetPassword as cognitoResetPassword,
  fetchAuthSession,
} from 'aws-amplify/auth';

import { answerPasskeyChallenge } from '../passkeys/webauthn.js';

export const AuthenticationContext = createContext(null);

// Runs one step of a passkey sign-in, and on failure names the step and the
// error in the console before passing the error on. The screen shows one message
// for several causes on purpose, so this is how a failure gets diagnosed. Only
// the error's name and message are logged: there is no password to echo.
async function passkeyStep(step, run) {
  try {
    return await run();
  } catch (error) {
    console.warn(
      `Passkey sign-in failed at ${step}:`,
      error?.name,
      error?.message,
    );
    throw error;
  }
}

// Consumer hook. Throws outside a provider so a mis-wired tree fails loudly at
// mount rather than silently reading a default and behaving as "signed out".
export function useAuth() {
  const context = useContext(AuthenticationContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthenticationProvider');
  }
  return context;
}

export function AuthenticationProvider({ children }) {
  const [authenticated, setAuthed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [user, setUser] = useState(null);
  const [currentAuthUser, setCurrentAuthUser] = useState(null);
  // The Cognito groups the account belongs to (the id token's cognito:groups):
  // what decides which development flags the user may switch on (see
  // flags/FlagContext). The app's spelling is kept so the value shape matches.
  const [eligableGroups, setEligableGroups] = useState([]);

  const fetchUser = useCallback(async function fetchUser(forceRefresh = false) {
    let currentUserData;
    try {
      const authSession = await fetchAuthSession({ forceRefresh });
      if (authSession.tokens) {
        const {
          email,
          sub,
          'cognito:groups': groups,
          given_name,
          family_name,
        } = authSession.tokens.idToken.payload;
        currentUserData = { sub, email, given_name, family_name, groups };
        setCurrentAuthUser(currentUserData);
        setAuthed(true);
        setEligableGroups(groups || []);
        setUser({ email, given_name, family_name });
      } else {
        setAuthed(false);
        setUser(null);
        setEligableGroups([]);
      }
    } catch {
      // Never log the error/session here: it can carry token material. A signed-in
      // check that throws simply means "not authenticated".
      setAuthed(false);
    } finally {
      setLoaded(true);
    }

    return currentUserData;
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const signIn = useCallback(
    async (emailAddress, password) => {
      let signInResult;
      try {
        signInResult = await cognitoSignIn({
          username: emailAddress.toLowerCase(),
          password,
        });
      } catch (ex) {
        // Do not log: a sign-in exception can echo the submitted credentials.
        throw ex;
      }

      if (
        signInResult.nextStep?.signInStep &&
        signInResult.nextStep.signInStep !== 'DONE'
      ) {
        if (signInResult.nextStep.signInStep === 'RESET_PASSWORD') {
          throw new Error('PasswordResetRequiredException');
        } else if (signInResult.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
          throw new Error('UserNotConfirmedException');
        } else {
          throw new Error('UnknownNextStep');
        }
      }

      return fetchUser();
    },
    [fetchUser],
  );

  // Signs in with a passkey through Cognito custom auth: Cognito issues a
  // WebAuthn challenge, the browser signs it with a passkey the user picks
  // (after their fingerprint, face or device PIN), and Cognito checks it.
  // Cognito needs the account's email to start, so the user types it first;
  // the app keeps the account it registered a passkey for instead.
  const signInWithPasskey = useCallback(
    async (emailAddress) => {
      const challenge = await passkeyStep('start (Cognito)', () =>
        cognitoSignIn({
          username: emailAddress.toLowerCase(),
          options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
        }),
      );

      const options =
        challenge.nextStep?.signInStep ===
          'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE' &&
        challenge.nextStep.additionalInfo?.options;
      if (!options) {
        console.warn(
          'Passkey sign-in got an unexpected step from Cognito:',
          challenge.nextStep?.signInStep,
        );
        throw new Error('UnknownNextStep');
      }

      const answer = await passkeyStep('passkey prompt (browser)', () =>
        answerPasskeyChallenge(JSON.parse(options)),
      );
      const result = await passkeyStep('passkey check (Cognito)', () =>
        cognitoConfirmSignIn({
          challengeResponse: JSON.stringify(answer),
        }),
      );

      // Cognito offers another attempt rather than failing outright when the
      // passkey isn't one of this account's (the browser may offer any Halo
      // passkey it holds, including another account's).
      if (result.nextStep?.signInStep !== 'DONE') {
        console.warn(
          'Passkey sign-in: Cognito did not accept the passkey:',
          result.nextStep?.signInStep,
        );
        throw new Error('PasskeyNotAccepted');
      }

      return fetchUser();
    },
    [fetchUser],
  );

  const signOut = useCallback(async () => {
    await cognitoSignOut();
    setAuthed(false);
    setUser(null);
    setCurrentAuthUser(null);
    setEligableGroups([]);
  }, []);

  // Re-check whether the Cognito session still exists, and sign out locally if it
  // has been revoked. Called when the live socket dies (see SessionGate), which is
  // the main signal a read-only client gets that a session may have ended.
  //
  // forceRefresh forces the refresh-token round-trip to Cognito: a NON-forced
  // fetch returns the locally-cached id/access tokens without a network call while
  // they are still unexpired (~60 min), so it would NOT notice a server-side
  // revocation until the tokens expired. With forceRefresh a revoked refresh token
  // makes amplify clear its stored tokens and return an empty session (no tokens)
  // -> we sign out immediately. A transient NETWORK failure still throws and is
  // caught below, so this does not add a false-sign-out risk on a flaky link.
  const revalidateSession = useCallback(async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      if (!session?.tokens) {
        // Definitively no session (revoked, nothing left to refresh) -> clear
        // local auth so the app returns to sign-in. Tokens are already gone from
        // the auth-storage seam at this point, so a cognitoSignOut is unnecessary.
        setAuthed(false);
        setUser(null);
        setCurrentAuthUser(null);
        setEligableGroups([]);
        return false;
      }
      return true;
    } catch {
      // Ambiguous (usually a transient network failure): do NOT sign the user out
      // on this alone — a revoked session returns empty tokens above, it does not
      // throw here.
      return true;
    }
  }, []);

  const forgotPassword = useCallback(async (emailAddress) => {
    // clientMetadata matches the mobile app: it drives the Cognito custom-message
    // Lambda that builds the reset email and its link to the /password-code
    // bridge, so the reset flow only works end-to-end with these exact values. A
    // web-specific ResetOrigin (e.g. 'HaloWeb') would need a matching branch
    // added to that Lambda first (backend change, TBD).
    await cognitoResetPassword({
      username: emailAddress.toLowerCase(),
      clientMetadata: { ResetOrigin: 'HaloApp', AccountType: 'Halo' },
    });
  }, []);

  const value = useMemo(
    () => ({
      authenticated,
      loaded,
      user: user || {},
      currentAuthUser,
      eligableGroups,
      signIn,
      signInWithPasskey,
      signOut,
      forgotPassword,
      revalidateSession,
    }),
    [
      authenticated,
      loaded,
      user,
      currentAuthUser,
      eligableGroups,
      signIn,
      signInWithPasskey,
      signOut,
      forgotPassword,
      revalidateSession,
    ],
  );

  return (
    <AuthenticationContext.Provider value={value}>
      {children}
    </AuthenticationContext.Provider>
  );
}
